// src/utils/services/Careplans/carePlanAiService.js
import { callOpenAIChat } from '../openaiClient';

/**
 * Safe JSON parser - strips markdown fences and extracts JSON object.
 */
const safeParseJson = (raw) => {
  if (!raw) throw new Error('No content to parse');

  let text = typeof raw === 'string' ? raw : String(raw);
  text = text.trim();

  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    throw new Error('No JSON object found in model response');
  }

  return JSON.parse(text.slice(firstBrace, lastBrace + 1));
};

/**
 * Build the clinical context block from notes + allergies + patient info.
 */
const buildCarePlanContext = ({
  patient = {},
  allergies = [],
  clinicalNotes = [],
  goals = '',
  habits = '',
}) => {
  const parts = [];

  // Demographics
  parts.push('===== PATIENT DEMOGRAPHICS =====');
  parts.push(`Name: ${patient.fullName || 'N/A'}`);
  parts.push(`Age: ${patient.age || 'N/A'}`);
  parts.push(`Gender: ${patient.gender || 'N/A'}`);
  parts.push(`DOB: ${patient.dob || 'N/A'}`);
  parts.push('');

  // Physician goals
  if (goals.trim()) {
    parts.push('===== PHYSICIAN GOALS FOR THIS CARE PLAN =====');
    parts.push(goals.trim());
    parts.push('');
  }

  // Habits registered by physician
  if (habits.trim()) {
    parts.push('===== PATIENT HABITS / LIFESTYLE (REPORTED BY PHYSICIAN) =====');
    parts.push(habits.trim());
    parts.push('');
  }

  // Allergies
  parts.push('===== ALLERGIES =====');
  if (!allergies || allergies.length === 0) {
    parts.push('No allergy records found.');
  } else {
    allergies.forEach((a, idx) => {
      const line = [
        `ALLERGY #${idx + 1}: ${a.allergy || 'Unknown'}`,
        a.reaction ? `Reaction: ${a.reaction}` : null,
        a.treatment ? `Treatment: ${a.treatment}` : null,
        a.status ? `Status: ${a.status}` : null,
      ]
        .filter(Boolean)
        .join(' | ');
      parts.push(line);
    });
  }
  parts.push('');

  // Clinical notes
  parts.push('===== CLINICAL NOTES =====');
  if (!clinicalNotes || clinicalNotes.length === 0) {
    parts.push('No clinical notes available.');
  } else {
    clinicalNotes.forEach((note, idx) => {
      const f = note.fields || {};
      const dateLabel = note.noteDateTime
        ? new Date(note.noteDateTime).toISOString()
        : 'N/A';
      const type = note._type || note.templateName || 'Clinical Note';

      parts.push(`--- NOTE #${idx + 1} – ${type} – ${dateLabel} ---`);

      const chiefComplaint =
        f.ChiefComplaints || f.ChiefComplaint || f.CC || '';
      if (chiefComplaint) parts.push(`Chief Complaint: ${chiefComplaint}`);

      const hpi =
        f.HPI || f.HistoryOfPresentIllness || f.History || f.HPI_Description || '';
      if (hpi) parts.push(`HPI: ${hpi}`);

      const pmh = f.PMH || f.MedicalHistory || f.PMH2 || '';
      if (pmh) parts.push(`Past Medical History: ${pmh}`);

      const assessment =
        f.Assessments || f.AssessmentAndPlan || f.Assessment || f.AP_VisitSummary || '';
      if (assessment) parts.push(`Assessment & Plan: ${assessment}`);

      const meds = f.CurrentMedications || f.Medications || f.Hx_CurrentMeds || '';
      if (meds) parts.push(`Medications: ${meds}`);

      const treatment = f.Treatment || f.Treatment1 || '';
      if (treatment) parts.push(`Treatment: ${treatment}`);

      const dx = f.DX_Diagnosis || '';
      if (dx) parts.push(`Diagnosis: ${dx}`);

      // Vitals if available
      const weight = f.Vitals_Weight || '';
      const bmi = f.Vitals_BMI || '';
      const bpSys = f.Vitals_BP_Systolic1 || '';
      const bpDia = f.Vitals_BP_Diastolic1 || '';
      if (weight || bmi || (bpSys && bpDia)) {
        const vitals = [
          weight ? `Weight: ${weight}` : null,
          bmi ? `BMI: ${bmi}` : null,
          bpSys && bpDia ? `BP: ${bpSys}/${bpDia}` : null,
        ]
          .filter(Boolean)
          .join(', ');
        parts.push(`Vitals: ${vitals}`);
      }

      parts.push('');
    });
  }

  let fullText = parts.join('\n');

  // Truncate for AI context window
  const MAX_CHARS = 14000;
  if (fullText.length > MAX_CHARS) {
    fullText =
      fullText.slice(0, MAX_CHARS) +
      '\n\n[TRUNCATED – remaining clinical data omitted for context limit]';
  }

  return fullText;
};

/**
 * Generate a comprehensive AI-powered Care Plan from clinical data.
 *
 * @param {Object} params
 * @param {Object} params.patient - { fullName, age, gender, dob }
 * @param {Array}  params.allergies - AdvancedMD allergies array
 * @param {Array}  params.clinicalNotes - Merged clinical notes (all 4 types)
 * @param {string} params.goals - Physician-stated goals for the care plan
 * @param {string} params.habits - Patient habits/lifestyle noted by physician
 * @returns {Promise<Object>} Structured care plan
 */
export const generateCarePlanFromAI = async ({
  patient = {},
  allergies = [],
  clinicalNotes = [],
  goals = '',
  habits = '',
}) => {
  const contextText = buildCarePlanContext({
    patient,
    allergies,
    clinicalNotes,
    goals,
    habits,
  });

  if (!contextText.trim()) {
    console.warn('[generateCarePlanFromAI] Empty context');
    return { carePlan: null };
  }

  const userPrompt = `
You are a board-certified physician assistant specialized in care coordination and chronic disease management.
Your task is to generate a **comprehensive, structured Care Plan** for the patient described below.

All output MUST be in ENGLISH.

You will receive:
- Patient demographics
- Physician-stated goals for this care plan
- Patient habits / lifestyle information
- Allergy records from the EHR (AdvancedMD)
- Clinical notes from multiple encounter types (Annual Wellness, Follow Up, Telehealth, Hospital Discharge)

===== INSTRUCTIONS =====

1. **Analyze** all clinical notes to identify active problems, chronic conditions, and risk factors.
2. **Incorporate** the physician's goals into the care plan.
3. **Consider** allergies when recommending any interventions (flag drug allergies if relevant).
4. **Factor in** patient habits/lifestyle for preventive recommendations.
5. **Generate** a structured care plan with the sections described below.

Return ONLY a JSON object with this exact structure:

{
  "summary": "A 2-3 sentence executive summary of the patient's clinical picture and care plan focus.",

  "identifiedProblems": [
    {
      "problem": "Name of the condition or problem",
      "icd10": "ICD-10 code if identifiable, or 'N/A'",
      "severity": "Mild | Moderate | Severe | Unknown",
      "status": "Active | Resolved | Monitoring",
      "clinicalEvidence": "Brief evidence from the notes supporting this problem"
    }
  ],

  "goals": [
    {
      "goal": "Clear, measurable goal statement",
      "targetDate": "YYYY-MM-DD or 'Ongoing'",
      "priority": "High | Medium | Low",
      "relatedProblem": "Which identified problem this goal addresses"
    }
  ],

  "interventions": [
    {
      "intervention": "Specific action or treatment step",
      "type": "Medication | Referral | Lab/Test | Education | Lifestyle | Monitoring | Procedure",
      "frequency": "e.g. 'Daily', 'Every 3 months', 'One-time', etc.",
      "responsibleParty": "PCP | Specialist | Patient | Care Manager | Nursing",
      "relatedGoal": "Which goal this intervention supports",
      "allergyAlert": "Flag if there is an allergy concern, otherwise null"
    }
  ],

  "allergySafetyNotes": [
    "Any specific allergy-related safety notes or drug contraindications identified"
  ],

  "preventiveRecommendations": [
    {
      "recommendation": "Preventive care action",
      "rationale": "Why this is recommended based on patient profile",
      "dueDate": "YYYY-MM-DD or 'As scheduled'"
    }
  ],

  "lifestyleModifications": [
    {
      "area": "Diet | Exercise | Smoking | Alcohol | Sleep | Stress | Other",
      "recommendation": "Specific lifestyle recommendation",
      "rationale": "Based on patient habits and clinical picture"
    }
  ],

  "followUp": {
    "nextVisit": "Recommended timeframe for next visit",
    "labsNeeded": ["List of labs to order before next visit"],
    "referrals": ["Specialist referrals recommended"],
    "patientEducation": ["Key education topics for the patient"]
  },

  "riskAssessment": {
    "overallRisk": "Low | Moderate | High | Critical",
    "riskFactors": ["List of identified risk factors"],
    "mitigationStrategies": ["Key strategies to reduce risk"]
  }
}

===== IMPORTANT RULES =====
- Return STRICT JSON ONLY. No markdown, no text outside the JSON.
- Base everything on the clinical evidence provided. Do NOT invent diagnoses.
- If data is insufficient for a section, include the section with an appropriate note.
- Keep interventions actionable and professional (not patient-facing education).
- DO NOT prescribe exact dosages. Use "consider", "evaluate", "optimize current therapy".
- Maximum 8 items per array to keep the plan focused.
- Prioritize the physician's stated goals.

===== CLINICAL CONTEXT =====

${contextText}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;

  if (!content) {
    console.error('[generateCarePlanFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJson(content);
    return { carePlan: json };
  } catch (err) {
    console.error('[generateCarePlanFromAI] JSON parse error:', err);
    console.error('[generateCarePlanFromAI] Raw content:', content);
    return { carePlan: null, rawContent: content };
  }
};
